import React, { useState, useEffect } from 'react';
import styles from './PropertyMessageContact.scss';
import useComponentWillMount from '../hooks/useComponentWillMount';
import { IUserState } from '../stores/useUser';
import TextField from './MaterialUi/TextField';
import RadioGroup from './MaterialUi/RadioGroup';
import EditIcon from '@mui/icons-material/Edit';
import Checkbox from './MaterialUi/Checkbox';
import Affiliate from '../utils/Affiliate';
import IHome from '../interfaces/IHome';
import JsonApiRequest, { IProperty, IRelation, IRelationB2b } from '../utils/JsonApiRequest';
import LogError from '../utils/LogError';

interface IPropertyMessageContact {
    data: IUserState;
    onChange: (ev: React.FormEvent) => void;
    hasDocuments: boolean;
    setWantFinancingOffer: (wantFinancingOffer: boolean) => void;
    wantFinancingOffer: boolean;
    marketingType: IHome['marketingType'];
    propertyId: string;
    setWantFinancialAdvisor: (wantFinancialAdvisor: boolean) => void;
    wantFinancialAdvisor: boolean;
}

const leadTexts = [
    {id: 'partners', text: <>Ich wünsche zusätzlich eine Finanzierungsberatung zu dieser Immobilie und möchte zu diesem Zweck Beratungsanfragen von den <a target="_blank" href="/info/servicepartner">hier</a> genannten Wohnglück-Berater:innen erhalten, die ich einzeln annehmen oder ablehnen kann. Ich bin damit einverstanden, dass mein Name und die angefragte Immobilie mit den Berater:innen geteilt werden. Weitere Daten werden erst nach meiner Annahme einer Berateranfrage geteilt. Ich kann mein Gesuch jederzeit zurückziehen.</>},
    {id: 'advisors', text: <>Ich wünsche zusätzlich eine Finanzierungsberatung zu dieser und ggf. weiteren von mir angefragten Immobilien und möchte zu diesem Zweck Beratungsanfragen von den <a target="_blank" href="/info/servicepartner">hier</a> genannten Wohnglück-Berater:innen erhalten, die ich einzeln annehmen oder ablehnen kann. Ich bin damit einverstanden, dass mein Name, die angefragte Immobilie, meine E-Mail-Adresse, meine Telefonnummer und meine optionalen Angaben zur Haushaltssituation mit den Berater:innen geteilt werden. Weitere Daten werden erst nach meiner Annahme einer Berateranfrage geteilt. Ich kann meine Anfrage jederzeit zurückziehen.</>},
    {id: 'affiliate', text: <>Ich wünsche ein Finanzierungsangebot zu dieser Immobilie von der in meinem Profil hinterlegten Hausbank.</>}
]

const PropertyMessageContact: React.FC<React.PropsWithChildren<IPropertyMessageContact>> = ({ data, onChange, hasDocuments, setWantFinancingOffer, wantFinancingOffer, marketingType, propertyId, setWantFinancialAdvisor, wantFinancialAdvisor }) => {
    const [userData, setUserData] = useState<IUserState>(data);
    const [isEditMode, setIsEditMode] = useState(true);
    const [relations, setRelations] = useState<Array<IRelation>>([]);
    const [relationsLength, setRelationsLength] = useState<number>(0);
    const [leadText, setLeadText] = useState<string>("");
    const [relationsB2b, setRelationsB2b] = useState<Array<IRelationB2b>>([]);

    useEffect(() => {
        const include = 'include=related_user,related_user.field_provider,related_user.field_provider.contact'
    
        JsonApiRequest.getRequest(`/user/user/${userData.id}/relations?${include}`)
            .then((res) => {
                const data = JsonApiRequest.data2Object<IRelation>(res.getData());
                if (data) {
                    setRelations(data)
                    if (data.length > relationsLength) setRelationsLength(relationsLength + 1)
                } else {
                    console.error('No Data found.', userData.id);
                    LogError.add(new Error('No Data found.'), {
                        title: 'Keine Daten gefunden',
                        error_code: 'PropertyMessageContact.tsx:useEffect',
                        request_data: { userData },
                    });
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }, [])

    useEffect(() => {
        const include = 'include=provider,provider.related_financial_service_providers,provider.related_financial_service_providers.related_user'
    
        JsonApiRequest.getRequest(`/real_estate_property/real_estate_property/${propertyId}?${include}`)
            .then((res) => {
                const data = JsonApiRequest.data2Object<IProperty>(res.getData());
                if (data) {
                    setRelationsB2b(data[0]?.provider?.related_financial_service_providers);
                } else {
                    console.error('No Data found.', propertyId);
                    LogError.add(new Error('No Data found.'), {
                        title: 'Keine Daten gefunden',
                        error_code: 'PropertyMessageContact.tsx:useEffect',
                        request_data: { propertyId },
                    });
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }, [])

    useEffect(() => {
        if (!hasBankAffiliate && !hasAdvisorsWithPermission && !hasRelationsB2b) {
            //checkbox text for SHW -> ECON
            setLeadText("partners");
            setWantFinancialAdvisor(false);
        } else if (!hasBankAffiliate && !hasAdvisorsWithPermission && hasRelationsB2b) {
            //checkbox text for relationB2b advisors of real estate agents
            setLeadText("advisors");
            setWantFinancialAdvisor(true);
        } else if (hasBankAffiliate && !hasAdvisorsWithPermission) {
            //checkbox text for affiliate -> ECON
            setLeadText("affiliate");
            setWantFinancialAdvisor(false);
            // setHasAffiliate(true);
        } else if (hasAdvisorsWithPermission) {
            //no lead, just text
            setLeadText("text");
            setWantFinancialAdvisor(false);
        }
    })

    const handleChange = (ev: React.FormEvent) => {
        onChange(ev);
    };

    useComponentWillMount(() => {
        if (
            isEditMode === true &&
            data.salutation &&
            data.given_name &&
            data.family_name &&
            data.address_line1 &&
            data.postal_code &&
            data.locality &&
            data.phone &&
            data.mail
        ) {
            setIsEditMode(false);
        }
    });

    useEffect(() => {
        setUserData(data);
    }, [data]);

    const toggleEditMode = () => {
        setIsEditMode(true);
    };

    const showAsAddress = !isEditMode;

    const hasBankAffiliate = Affiliate.isBankAffiliate(data.affiliateName);
    const hasRelationsB2b = relationsB2b?.length > 0;

    const advisorsWithPermission = relations?.filter((r) => r.share_saved_searches_prop_bookmarks_prop_messages);
    const hasAdvisorsWithPermission = advisorsWithPermission?.length > 0;

    return (
        <div className={styles.propertyMessageContact}>
            {showAsAddress ? (
                <div className={styles.address}>
                    {data.salutation === 'male' ? 'Herr' : data.salutation === 'female' ? 'Frau' : ''} {data.given_name} {data.family_name}
                    <br />
                    {data.address_line1}
                    <br />
                    {data.postal_code} {data.locality}
                    <br />
                    Telefon: {data.phone}
                    <br />
                    Email: {data.mail}
                    <p>
                        <a onClick={toggleEditMode}>
                            <EditIcon fontSize="small" /> Kontaktdaten ändern
                        </a>
                    </p>
                </div>
            ) : (
                <div className={styles.fields}>
                    <div className={styles.salutation}>
                        <RadioGroup
                            options={[{ label: 'Herr', value: 'male' }, { label: 'Frau', value: 'female' }, { label: 'divers', value: 'diverse' }]}
                            defaultValue="male"
                            value={userData.salutation}
                            onChange={handleChange}
                            name="salutation"
                            required={true}
                            row
                        />
                    </div>

                    <div className={styles.name}>
                        <TextField name="given_name" value={userData.given_name || ''} onChange={handleChange} label="Vorname" required={true} />
                    </div>
                    <div className={styles.name}>
                        <TextField name="family_name" value={userData.family_name || ''} onChange={handleChange} label="Nachname" required={true} />
                    </div>
                    <div className={styles.wide}>
                        <TextField
                            name="address_line1"
                            value={userData.address_line1 || ''}
                            onChange={handleChange}
                            label="Straße und Hausnummer"
                            required={true}
                        />
                    </div>
                    <div className={styles.postalCode}>
                        <TextField
                            name="postal_code"
                            value={userData.postal_code || ''}
                            onChange={handleChange}
                            label="Postleitzahl"
                            required={true}
                        // inputMode="numeric"
                        // pattern="[0-9]{5}"
                        />
                    </div>
                    <div className={styles.locality}>
                        <TextField name="locality" value={userData.locality || ''} onChange={handleChange} label="Stadt" required={true} />
                    </div>
                    <div className={styles.wide}>
                        <TextField name="phone" value={userData.phone || ''} onChange={handleChange} label="Telefon" required={true} />
                    </div>
                    <div className={styles.wide}>
                        <TextField name="mail" type="email" value={userData.mail || ''} onChange={handleChange} label="E-Mail" required={true} disabled={!userData.isCommercialProvider && !userData.isFinanzvertrieb} />
                    </div>
                </div>
            )}

            {marketingType === 'buy' && (
                <div className={styles.financingOffer}>
                    {leadText === "text" ? (
                        <p>Hinweis: Du teilst derzeit Informationen zu angefragten Immobilien mit deinen verknüpften Berater:innen, 
                            damit diese dir Finanzierbarkeitsindikationen senden können. 
                            Du kannst diese Einstellung in deinem Profil jederzeit anpassen.</p>
                    ):(
                        <Checkbox
                            value={wantFinancingOffer || false}
                            name="financing_offer"
                            label={leadTexts.find((l) => l.id === leadText)?.text}
                            onChange={() => {setWantFinancingOffer(!wantFinancingOffer);}} />
                    )
                    }
                </div>
            )}

            {/* {hasDocuments && (
                <div className={styles.sendApplication}>
                    <Checkbox checked={true} value="true" name="sendApplication" label="Bewerbermappe mitschicken" onChange={handleCheckboxChange} />
                </div>
            )} */}
        </div>
    );
};

export default PropertyMessageContact;
